import { useEffect } from "react";

import type { LocaleObject } from "yup";

import { printValue, setLocale, ValidationError } from "@lib/yup";

import { useI18nLocale } from "@services/useI18nLocale";
import useI18nNamespace from "@services/useI18nNamespace";

/**
 * This localizes the error messages whenever the locale is changed
 */
export const useLocalizeYup = () => {
  const { t } = useI18nNamespace("yup");
  const locale = useI18nLocale();

  useEffect(() => {
    const formatter = new Intl.ListFormat(new Intl.Locale(locale), { style: "long", type: "conjunction" });
    // values grabbed from https://github.com/jquense/yup/blob/master/src/locale.ts
    const mixed: LocaleObject["mixed"] = {
      default: ({ path }) => t("mixed-default-error-message", "{path} is invalid", { path }),
      required: ({ path }) => t("mixed-required-error-message", "{path} is a required field", { path }),
      defined: ({ path }) => t("mixed-defined-error-message", "{path} must be defined", { path }),
      notNull: ({ path }) => t("mixed-not-null-error-message", "{path} cannot be null", { path }),
      oneOf: ({ path, values }) => {
        const valueList = formatter.format(values);
        return t("mixed-one-of-error-message", "{path} must be one of the following values: {valueList}", {
          path,
          valueList,
        });
      },
      notOneOf: ({ path, values }) => {
        const valueList = formatter.format(values);
        return t("mixed-not-one-of-error-message", "{path} must be not one of the following values: {valueList}", {
          path,
          valueList,
        });
      },
      notType: ({ path, type, value, originalValue }) => {
        const formattedOriginalValue = printValue(originalValue, true);
        const formattedValue = printValue(value, true);
        if (originalValue != null && originalValue !== value) {
          if (type !== "mixed") {
            return t(
              "mixed-not-type-not-mixed-cast-error-message",
              '{path} must be a "{type}" type, but the final value was: "{formattedValue}" (cast from the value "{formattedOriginalValue}").',
              {
                path,
                type,
                formattedValue,
                formattedOriginalValue,
              }
            );
          }
          return t(
            "mixed-not-type-mixed-cast-error-message",
            '{path} must match the configured type. The validated value was: "{formattedValue}" (cast from the value "{formattedOriginalValue}").',
            {
              path,
              formattedValue,
              formattedOriginalValue,
            }
          );
        }
        if (type !== "mixed") {
          return t(
            "mixed-not-type-not-mixed-no-cast-error-message",
            '{path} must be a "{type}" type, but the final value was: "{formattedValue}".',
            {
              path,
              type,
              formattedValue,
            }
          );
        }
        return t(
          "mixed-not-type-mixed-no-cast-error-message",
          '{path} must match the configured type. The validated value was: "{formattedValue}".',
          {
            path,
            formattedValue,
          }
        );
      },
    };
    setLocale({
      mixed,
      string: {
        length: ({ path, length }) =>
          t("string-length-error-message", "{path} must be exactly {length, number} characters", { path, length }),
        min: ({ path, min }) =>
          t("string-min-error-message", "{path} must be at least {min, number} characters", { path, min }),
        max: ({ path, max }) =>
          t("string-max-error-message", "{path} must be at most {max, number} characters", { path, max }),
        matches: ({ path, regex }) =>
          t("string-matches-error-message", "{path} must match the following: {regex}", {
            path,
            regex: regex.toString(),
          }),
        email: ({ path }) => t("string-email-error-message", "{path} must be a valid email", { path }),
        uuid: ({ path }) => t("string-uuid-error-message", "{path} must be a valid UUID", { path }),
        url: ({ path }) => t("string-url-error-message", "{path} must be a valid URL", { path }),
        trim: ({ path }) => t("string-trim-error-message", "{path} must be a trimmed string", { path }),
        lowercase: ({ path }) => t("string-lowercase-error-message", "{path} must be a lowercase string", { path }),
        uppercase: ({ path }) =>
          t("string-uppercase-error-message", "{path} must be a upper case string", { path }),
        // @ts-expect-error Yup doesn't allow overriding the built-in locales in TS
        bsonid: ({ path }) => t("string-bsonid-error-message", "{path} must be a valid BSON id", { path }),
      },
      number: {
        min: ({ path, min }) =>
          t("number-min-error-message", "{path} must be greater than or equal to {min, number}", { path, min }),
        max: ({ path, max }) =>
          t("number-max-error-message", "{path} must be less than or equal to {max, number}", { path, max }),
        lessThan: ({ path, less }) =>
          t("number-less-than-error-message", "{path} must be less than {less, number}", { path, less }),
        moreThan: ({ path, more }) =>
          t("number-more-than-error-message", "{path} must be greater than {more, number}", { path, more }),
        positive: ({ path }) => t("number-positive-error-message", "{path} must be a positive number", { path }),
        negative: ({ path }) => t("number-negative-error-message", "{path} must be a negative number", { path }),
        integer: ({ path }) => t("number-integer-error-message", "{path} must be an integer", { path }),
      },
      date: {
        min: ({ path, min }) =>
          t(
            "date-min-error-message",
            "{path} field must be later than {min, date, braze-datetime-from-iso-string}",
            { path, min }
          ),
        max: ({ path, max }) =>
          t(
            "date-max-error-message",
            "{path} field must be at earlier than {max, date, braze-datetime-from-iso-string}",
            { path, max }
          ),
      },
      boolean: {
        isValue: ({ path, value }) =>
          t("boolean-is-value-error-message", "{path} must be {value}", { path, value }),
      },
      object: {
        noUnknown: ({ path, unknown }) => {
          const unknownList = formatter.format(unknown);
          return t("object-no-unknown-error-message", "{path} field has unspecified keys: {unknownList}", {
            path,
            unknownList,
          });
        },
      },
      array: {
        min: ({ path, min }) =>
          t("array-min-error-message", "{path} field must have at least {min, number} items", { path, min }),
        max: ({ path, max }) =>
          t("array-max-error-message", "{path} field must have less than or equal to {max, number} items", {
            path,
            max,
          }),
        length: ({ path, length }) =>
          t("array-length-error-message", "{path} field must have {length, number} items", { path, length }),
      },
      tuple: {
        notType: (params) => {
          const { path, value, spec } = params;
          const typeLen = spec.types.length;
          const formattedValue = printValue(value, true);
          if (Array.isArray(value)) {
            if (value.length < typeLen) {
              return t(
                "tuple-not-type-value-less-then-typelen",
                "{path} tuple value has too few items, expected a length of {typeLen, number} but got {len, number} for value: `{formattedValue}``",
                {
                  path,
                  typeLen,
                  formattedValue,
                  len: value.length,
                }
              );
            }
            if (value.length > typeLen) {
              return t(
                "tuple-not-type-value-greater-then-typelen",
                "{path} tuple value has too many items, expected a length of {typeLen, number} but got {len, number} for value: `{formattedValue}``",
                {
                  path,
                  typeLen,
                  formattedValue,
                  len: value.length,
                }
              );
            }
          }

          return ValidationError.formatError(mixed.notType, params);
        },
      },
    });
  }, [t, locale]);
};
